import { useId, useState, type ReactNode } from 'react';
import { useTenantList } from '../../api/tenants';
import {
  useCreateUser,
  useDeactivateUser,
  useDeleteUser,
  useReactivateUser,
  useUpdateUser,
  useUsers,
  type NewUser,
} from '../../api/users';
import { errorMessage } from '../../api/client';
import {
  displayName,
  MEMBER_ROLES,
  ROLE_LABELS,
  STAFF_ROLES,
  type Role,
  type StaffRole,
  type User,
  type UserStatus,
  type UserType,
} from '../../api/types';
import { useMe } from '../../auth/AuthContext';
import { EmptyState, ErrorState, SkeletonTable } from '../../components/states';
import { DataTable } from '../../components/table/DataTable';
import {
  Alert,
  Badge,
  Button,
  Modal,
  PageHeader,
  Section,
  SelectField,
  TextField,
  formatDate,
  statusLabel,
  statusTone,
} from '../../components/ui';
import { formatNumber } from '../../format';

const PAGE = 50;
const STATUSES: UserStatus[] = ['PENDING', 'ACTIVE', 'INACTIVE'];
/** Enough to send an invitation to; the API validates properly. */
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE = /^\+[1-9]\d{6,14}$/;

type Notice = { kind: 'success' | 'error'; text: ReactNode };

/** The roles an admin may put on an account of this type. */
function rolesFor(userType: UserType): readonly Role[] {
  return userType === 'staff' ? STAFF_ROLES : MEMBER_ROLES;
}

/**
 * /staff/users — the whole account directory, admins only: staff and tenant
 * accounts alike, created, edited, switched off and deleted from here. The
 * Team page keeps its narrower staff-only view. A staff account is invited
 * through /users/invite; a tenant account joins a tenant (/tenants/:id/members).
 * Roles are set when the account is created and are not edited from the list.
 */
export function Users() {
  const me = useMe();
  const [offset, setOffset] = useState(0);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [creating, setCreating] = useState(false);

  const users = useUsers({ limit: PAGE, offset });
  const tenants = useTenantList({ limit: 100 });
  const page = users.data;
  const tenantOptions = tenants.data?.data ?? [];

  return (
    <>
      <PageHeader
        title="Users"
        actions={<Button onClick={() => setCreating(true)}>Create user</Button>}
      />
      {notice ? <Alert kind={notice.kind}>{notice.text}</Alert> : null}

      <Section
        title="All accounts"
        meta={page ? `${formatNumber(page.pagination.total)} · newest first` : undefined}
      >
        {users.isError ? (
          <ErrorState
            error={users.error}
            onRetry={() => users.refetch()}
            retrying={users.isFetching}
          />
        ) : null}
        {users.isPending ? <SkeletonTable rows={4} cols={7} /> : null}
        {page && page.data.length === 0 ? (
          <EmptyState
            title="No users yet"
            body="Accounts appear here as staff are invited and tenants sign up."
          />
        ) : null}
        {page && page.data.length > 0 ? (
          <DataTable
            label="Users"
            columns={[
              { key: 'name', header: 'Name' },
              { key: 'email', header: 'Email' },
              { key: 'type', header: 'Type' },
              { key: 'role', header: 'Role' },
              { key: 'status', header: 'Status' },
              { key: 'sign_in', header: 'Sign-in' },
              { key: 'added', header: 'Added', align: 'num' },
              { key: 'actions', header: 'Actions', srOnly: true },
            ]}
          >
            {page.data.map((u) => (
              <UserRow
                key={u.id}
                user={u}
                self={u.id === me.user.id}
                onChanged={(text, kind = 'success') => setNotice({ kind, text })}
              />
            ))}
          </DataTable>
        ) : null}
        {page && (offset > 0 || page.pagination.hasMore) ? (
          <div className="row-actions">
            <Button
              variant="ghost"
              className="btn--small"
              disabled={offset === 0 || users.isPlaceholderData}
              onClick={() => setOffset(Math.max(0, offset - PAGE))}
            >
              Newer
            </Button>
            <Button
              variant="ghost"
              className="btn--small"
              disabled={!page.pagination.hasMore || users.isPlaceholderData}
              onClick={() => setOffset(offset + PAGE)}
            >
              Older
            </Button>
          </div>
        ) : null}
      </Section>

      {creating ? (
        <CreateUserModal
          tenants={tenantOptions.map((t) => ({ id: t.id, name: t.name }))}
          onClose={() => setCreating(false)}
          onDone={(user) => {
            setCreating(false);
            setNotice({
              kind: 'success',
              text: (
                <>
                  Created <strong>{displayName(user)}</strong> as {ROLE_LABELS[user.role]}; a
                  temporary password was emailed to {user.email}.
                </>
              ),
            });
          }}
        />
      ) : null}
    </>
  );
}

/**
 * One form for both kinds of account. The type decides the role list and,
 * for a tenant account, asks which organization it joins. Mounted only while
 * open, so closing it drops what was typed and any error from the last attempt.
 */
function CreateUserModal({
  tenants,
  onClose,
  onDone,
}: {
  tenants: Array<{ id: string; name: string }>;
  onClose: () => void;
  onDone: (user: User) => void;
}) {
  const [userType, setUserType] = useState<UserType>('staff');
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [role, setRole] = useState<Role>('support');
  const [tenantId, setTenantId] = useState(tenants[0]?.id ?? '');
  const formId = useId();
  const create = useCreateUser();
  const error = create.error ? errorMessage(create.error) : null;

  const roles = rolesFor(userType);
  const switchType = (next: UserType) => {
    setUserType(next);
    setRole(next === 'staff' ? 'support' : 'member');
  };
  const valid =
    EMAIL.test(email.trim()) &&
    (userType === 'staff' ||
      (tenantId !== '' && firstName.trim() !== '' && lastName.trim() !== ''));

  const close = () => {
    if (!create.isPending) onClose();
  };
  const submit = () => {
    if (!valid || create.isPending) return;
    const names = {
      ...(firstName.trim() ? { firstName: firstName.trim() } : {}),
      ...(lastName.trim() ? { lastName: lastName.trim() } : {}),
    };
    const body: NewUser =
      userType === 'staff'
        ? { userType, email: email.trim(), role: role as StaffRole, ...names }
        : {
            userType,
            email: email.trim(),
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            role: role as 'owner' | 'member',
            tenantId,
          };
    create.mutate(body, { onSuccess: onDone });
  };

  return (
    <Modal
      open
      onClose={close}
      title="Create user"
      footer={
        <>
          <Button type="button" variant="ghost" onClick={close} disabled={create.isPending}>
            Cancel
          </Button>
          <Button type="submit" form={formId} loading={create.isPending} disabled={!valid}>
            {userType === 'staff' ? 'Invite staff account' : 'Invite tenant account'}
          </Button>
        </>
      }
    >
      {error ? <Alert kind="error">{error}</Alert> : null}
      <form
        id={formId}
        className="form"
        aria-label="Create a user"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
        noValidate
      >
        <SelectField
          label="Account type"
          value={userType}
          onChange={(e) => switchType(e.target.value as UserType)}
          hint={
            userType === 'staff'
              ? 'A Balsam console account with a role.'
              : 'An account on one organization; the tenant keeps at most three.'
          }
        >
          <option value="staff">Staff</option>
          <option value="tenant">Tenant</option>
        </SelectField>
        <TextField
          label="Email"
          name="newUserEmail"
          type="email"
          autoComplete="off"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <TextField
          label="First name"
          name="newUserFirstName"
          autoComplete="off"
          required={userType === 'tenant'}
          value={firstName}
          hint={
            userType === 'staff' ? 'Optional: derived from the email when left out.' : undefined
          }
          onChange={(e) => setFirstName(e.target.value)}
        />
        <TextField
          label="Last name"
          name="newUserLastName"
          autoComplete="off"
          required={userType === 'tenant'}
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
        />
        <SelectField label="Role" value={role} onChange={(e) => setRole(e.target.value as Role)}>
          {roles.map((r) => (
            <option key={r} value={r}>
              {ROLE_LABELS[r]}
            </option>
          ))}
        </SelectField>
        {userType === 'tenant' ? (
          <SelectField
            label="Tenant"
            value={tenantId}
            onChange={(e) => setTenantId(e.target.value)}
            hint={tenants.length === 0 ? 'Onboard a tenant first.' : undefined}
          >
            {tenants.length === 0 ? <option value="">No tenants yet</option> : null}
            {tenants.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </SelectField>
        ) : null}
      </form>
    </Modal>
  );
}

function UserRow({
  user,
  self,
  onChanged,
}: {
  user: User;
  /** The signed-in admin: role, deactivation and deletion are off for oneself. */
  self: boolean;
  onChanged: (text: ReactNode, kind?: 'success' | 'error') => void;
}) {
  const deactivate = useDeactivateUser();
  const reactivate = useReactivateUser();
  const remove = useDeleteUser();
  const [editing, setEditing] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const toggle = () => {
    const onSuccess = (next: User) =>
      onChanged(
        <>
          <strong>{displayName(next)}</strong>{' '}
          {next.isDeactivated ? 'is deactivated' : 'is active again'}.
        </>,
      );
    if (user.isDeactivated) reactivate.mutate(user.id, { onSuccess });
    else deactivate.mutate(user.id, { onSuccess });
  };
  const del = () =>
    remove.mutate(user.id, {
      onSuccess: () => {
        setConfirmingDelete(false);
        onChanged(
          <>
            Deleted <strong>{displayName(user)}</strong> ({user.email}).
          </>,
        );
      },
    });

  const toggling = deactivate.isPending || reactivate.isPending;
  const error = deactivate.error ?? reactivate.error ?? remove.error;

  return (
    <>
      <tr>
        <td>
          {displayName(user)}
          {self ? <span className="muted"> (you)</span> : null}
        </td>
        <td>{user.email}</td>
        <td>
          <Badge tone={user.userType === 'staff' ? 'accent' : 'neutral'}>
            {user.userType === 'staff' ? 'Staff' : 'Tenant'}
          </Badge>
        </td>
        <td>{ROLE_LABELS[user.role]}</td>
        <td>
          {user.isDeactivated ? (
            <Badge tone="bad">Deactivated</Badge>
          ) : (
            <Badge tone={statusTone(user.status)}>{statusLabel(user.status)}</Badge>
          )}
        </td>
        <td>
          <Badge tone={user.isProviderEnabled ? 'ok' : 'bad'}>
            {user.isProviderEnabled ? 'Enabled' : 'Disabled'}
          </Badge>
        </td>
        <td className="num">{formatDate(user.createdAt)}</td>
        <td>
          <div className="row-actions">
            <Button
              variant="ghost"
              className="btn--small"
              onClick={() => setEditing(true)}
              aria-label={`Edit ${displayName(user)}`}
              data-primary
            >
              Edit
            </Button>
            {!self ? (
              <>
                <Button
                  variant={user.isDeactivated ? 'secondary' : 'ghost'}
                  className="btn--small"
                  loading={toggling}
                  onClick={toggle}
                  aria-label={`${user.isDeactivated ? 'Reactivate' : 'Deactivate'} ${displayName(user)}`}
                >
                  {user.isDeactivated ? 'Reactivate' : 'Deactivate'}
                </Button>
                <Button
                  variant="danger"
                  className="btn--small"
                  onClick={() => setConfirmingDelete(true)}
                  aria-label={`Delete ${displayName(user)}`}
                >
                  Delete
                </Button>
              </>
            ) : null}
          </div>
        </td>
      </tr>
      {error && !editing && !confirmingDelete ? (
        <tr className="table__detail">
          <td colSpan={8}>
            <Alert kind="error">{errorMessage(error)}</Alert>
          </td>
        </tr>
      ) : null}
      {editing ? (
        <EditUserModal
          user={user}
          onClose={() => setEditing(false)}
          onDone={(next) => {
            setEditing(false);
            onChanged(
              <>
                Saved <strong>{displayName(next)}</strong>.
              </>,
            );
          }}
        />
      ) : null}
      {confirmingDelete ? (
        <Modal
          open
          onClose={() => !remove.isPending && setConfirmingDelete(false)}
          title="Delete this account?"
          footer={
            <>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setConfirmingDelete(false)}
                disabled={remove.isPending}
              >
                Keep it
              </Button>
              <Button type="button" variant="danger" loading={remove.isPending} onClick={del}>
                Delete {displayName(user)}
              </Button>
            </>
          }
        >
          {remove.error ? <Alert kind="error">{errorMessage(remove.error)}</Alert> : null}
          <p>
            <strong>{displayName(user)}</strong> ({user.email}) is removed from the hub and can no
            longer sign in. The row stays for audit. To only pause the account, deactivate it
            instead.
          </p>
        </Modal>
      ) : null}
    </>
  );
}

/** Email, phone and status. Mounted only while open. */
function EditUserModal({
  user,
  onClose,
  onDone,
}: {
  user: User;
  onClose: () => void;
  onDone: (user: User) => void;
}) {
  const [email, setEmail] = useState(user.email);
  const [phone, setPhone] = useState(user.phone ?? '');
  const [status, setStatus] = useState<UserStatus>(user.status);
  const formId = useId();
  const update = useUpdateUser();
  const error = update.error ? errorMessage(update.error) : null;

  const emailOk = EMAIL.test(email.trim());
  const phoneOk = phone.trim() === '' || PHONE.test(phone.trim());
  const changed =
    email.trim() !== user.email || (phone.trim() || null) !== user.phone || status !== user.status;
  const valid = emailOk && phoneOk && changed;

  const close = () => {
    if (!update.isPending) onClose();
  };
  const submit = () => {
    if (!valid || update.isPending) return;
    update.mutate(
      {
        id: user.id,
        body: {
          version: user.version,
          ...(email.trim() !== user.email ? { email: email.trim() } : {}),
          ...(phone.trim() && phone.trim() !== user.phone ? { phone: phone.trim() } : {}),
          ...(status !== user.status ? { status } : {}),
        },
      },
      { onSuccess: onDone },
    );
  };

  return (
    <Modal
      open
      onClose={close}
      title={`Edit ${displayName(user)}`}
      footer={
        <>
          <Button type="button" variant="ghost" onClick={close} disabled={update.isPending}>
            Cancel
          </Button>
          <Button type="submit" form={formId} loading={update.isPending} disabled={!valid}>
            Save
          </Button>
        </>
      }
    >
      {error ? <Alert kind="error">{error}</Alert> : null}
      <form
        id={formId}
        className="form"
        aria-label={`Edit ${displayName(user)}`}
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
        noValidate
      >
        <TextField
          label="Email"
          name="editEmail"
          type="email"
          autoComplete="off"
          required
          value={email}
          error={emailOk ? null : 'Enter a valid email address.'}
          onChange={(e) => setEmail(e.target.value)}
        />
        <TextField
          label="Phone"
          name="editPhone"
          autoComplete="off"
          placeholder="+14155552671"
          value={phone}
          hint="E.164. Changing it here does not update the provider; verification resets on sync."
          error={phoneOk ? null : 'Use the international format, e.g. +14155552671.'}
          onChange={(e) => setPhone(e.target.value)}
        />
        <SelectField
          label="Status"
          value={status}
          onChange={(e) => setStatus(e.target.value as UserStatus)}
          hint="INACTIVE stops the account from signing in to the hub; the last active admin cannot be made inactive."
        >
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {statusLabel(s)}
            </option>
          ))}
        </SelectField>
      </form>
    </Modal>
  );
}
